import { useCallback, useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import MenuBar from "./menu-bar";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import Blockquote from "@tiptap/extension-blockquote";
import Link from "@tiptap/extension-link";
import HtmlEditor from "./html-editor";
import {
  type EditTypesEnum,
  editTypesEnum,
  pageTypesConst,
} from "@/types/entities/legal";
import { IndentedParagraph } from "./indent-paragraph";
import { useLegalStore } from "@/stores/legal/useLegalStore";
import dayjs from "dayjs";
import { sanitizeHTML, cleanTitap } from "@/utils/format";
import { IndentedBulletList } from "./indent-bullet-list";
import { IndentedOrderedList } from "./indent-order-list";
import BasicMenuBar from "./basic-menu-bar";
// import Indent from '@tiptap/extension-indent';

interface RichTextEditorProps {
  editionType: EditTypesEnum;
  basic?: boolean;
  value?: (html: string) => void;
}

export default function RichTextEditor({
  editionType,
  basic = false,
  value,
}: RichTextEditorProps) {
  const {
    selected,
    setSelected,
    policies,
    terms,
    typeSelected,
    isValid,
    setIsValid,
  } = useLegalStore();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // heading: {
        //   levels: [1, 2, 3],
        //   HTMLAttributes: {
        //     class: "font-bold my-2 text-3xl",
        //   },
        // },
        bulletList: {
          HTMLAttributes: {
            class: "list-disc ml-3",
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: "list-decimal ml-3",
          },
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Blockquote,
      Highlight,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
      IndentedParagraph,
      IndentedBulletList,
      IndentedOrderedList,
      TextStyle,
    ],

    content: selected.content,
    editorProps: {
      attributes: {
        class: "min-h-[156px] border rounded-md bg-neutral-object py-2 px-3",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // console.log(html);
      value?.(html);
      onChange(cleanTitap(html));
    },
  });

  const onChange = (newContent: string) => {
    setSelected({
      ...selected,
      content: cleanTitap(newContent),
    });
  };

  const handleHtlmContentChange = useCallback(
    (newContent: string) => {
      setSelected({ ...selected, content: cleanTitap(newContent) });
    },
    [selected, setSelected],
  );

  useEffect(() => {
    if (editor) {
      if (selected.content !== editor.getHTML()) {
        if (selected.content) {
          editor.commands.setContent(cleanTitap(selected.content), false);
        } else editor.commands.setContent("", false);
      }
    }
  }, [selected.content, editor]);

  if (!editor || selected.content === undefined || selected.content === null)
    return null;

  const versions = typeSelected === pageTypesConst.policies ? policies : terms;

  const changeVersion = (id: string) => {
    if (typeSelected === pageTypesConst.policies) {
      const pol = policies.find((policy) => policy.id === id);
      if (pol) setSelected({ ...pol, content: sanitizeHTML(pol.content) });
    } else {
      const ter = terms.find((term) => term.id === id);
      if (ter) setSelected({ ...ter, content: sanitizeHTML(ter.content) });
    }
  };

  return (
    <div className="flex flex-col w-full">
      <div
        className={
          basic
            ? "relative gap-2 flex flex-col 2xl:flex-row justify-center 2xl:justify-between p-3 bg-neutral-object-accent-quiet rounded-tl-xl rounded-tr-xl w-full border-b border-neutral"
            : "relative gap-2 flex flex-col 2xl:flex-row justify-center 2xl:justify-between p-4 w-full border-b border-neutral"
        }
      >
        {basic ? (
          <BasicMenuBar
            editor={editor}
            isDisabled={editionType === editTypesEnum.html}
          />
        ) : (
          <MenuBar
            editor={editor}
            isDisabled={editionType === editTypesEnum.html}
          />
        )}

        {versions.length > 0 && (
          <div className="flex items-center gap-2 justify-center 2xl:justify-end w-full 2xl:w-auto">
            <span className="font-semibold text-sm text-heading-loud">
              Versión:{" "}
            </span>
            <select
              className="bg-neutral-object border border-neutral py-1 px-2 rounded-lg min-w-8 font-semibold text-sm text-body"
              onChange={(e) => {
                changeVersion(e.target.value);
              }}
            >
              {versions.map((version) => (
                <option
                  key={version.id + "-" + version.version}
                  value={version.id}
                >
                  {dayjs(new Date(version.created_at)).format("MM/DD/YYYY")}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {editionType === editTypesEnum.visual && (
        <EditorContent
          editor={editor}
          className="rich-text-content text-body-loud"
          data-testid="tiptap-editor"
        />
      )}
      {editionType === editTypesEnum.html && (
        <HtmlEditor
          content={selected.content}
          onContentChange={handleHtlmContentChange}
          onValidationChange={(value) => {
            if (value !== isValid) setIsValid(value);
          }}
        />
      )}
    </div>
  );
}
